import { z } from 'zod';

import { buildFormSchema } from '@/utils/validation/formSchemaKit';
import {
  buildCommonValidationMessages,
  emailField,
  nullableOptionalStringOrNumber,
  optionalString,
  requiredBoolean,
  requiredPresentField,
  requiredString,
} from '@/utils/validation/schemaHelpers';

import type { FormSchema } from '@/utils/validation/formSchemaKit';
import type { ValidatedField } from '@/utils/validation/schemaHelpers';
import type { TFunction } from 'i18next';

const optionalStringOrNumber = (): ValidatedField => ({
  schema: nullableOptionalStringOrNumber,
  isRequired: false,
});

export const buildDisputeInvoiceFormSchema = (t: TFunction): FormSchema => {
  return buildFormSchema({
    dispute_reason: requiredString(t('PLEASE_SELECT_REASON')),
    dispute_details: requiredString(t('PLEASE_PROVIDE_DETAILS')),
  });
};

export const buildRecruiterCompanyTypeFormSchema = (
  t: TFunction,
): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema(
    {
      recruiter_type: requiredString(messages.required),
      company_name: {
        schema: optionalString().schema,
        isRequired: (values: Record<string, unknown>) =>
          values.recruiter_type === 'agency',
      },
      about_company: optionalString(),
      default_currency: optionalString(),
      company_banner_image: optionalString(),
    },
    (schema) =>
      schema.superRefine((data, ctx) => {
        if (
          data.recruiter_type === 'agency' &&
          !data.company_name?.toString().trim()
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['company_name'],
            message: messages.required,
          });
        }
      }),
  );
};

export const buildAddCompanyRecruiterFormSchema = (
  t: TFunction,
  options?: { requireAgencyCompany?: boolean },
): FormSchema => {
  const requireAgencyCompany = options?.requireAgencyCompany ?? false;
  return buildFormSchema(
    {
      email: emailField({
        required: t('ERROR_PLEASE_INPUT_EMAIL'),
        invalidEmail: t('ERROR_PLEASE_ENTER_VALID_EMAIL'),
      }),
      name: requiredString(t('ERROR_PLEASE_INPUT_NAME')),
      agency_company: {
        schema: z.string().optional(),
        isRequired: false,
      },
    },
    (schema) =>
      schema.superRefine((data, ctx) => {
        if (!requireAgencyCompany) return;
        const value = String(
          (data as { agency_company?: string }).agency_company ?? '',
        ).trim();
        if (!value) {
          ctx.addIssue({
            code: 'custom',
            path: ['agency_company'],
            message: t('RM_SELECT_AGENCY_COMPANY'),
          });
        }
      }),
  );
};

export const buildCompanyInvitationEmailFormSchema = (
  t: TFunction,
): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    email: emailField(messages),
  });
};

export const buildEditInvoiceFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    invoice_reference: requiredString(messages.required),
    candidate_name: requiredString(messages.required),
    job_title: requiredString(messages.required),
    startdate_candidate: requiredPresentField(messages.required),
    amount_currency: optionalString(),
    contract_value: optionalStringOrNumber(),
    fee_type: optionalString(),
    amount: optionalStringOrNumber(),
    recruitment_fee_details: optionalString(),
    fee_percentage: optionalStringOrNumber(),
    fee_flat_amount: optionalStringOrNumber(),
    rate_frequency: optionalString(),
    contract_length: optionalStringOrNumber(),
    rate_amount: optionalStringOrNumber(),
  });
};

export const buildRecruiterTermsFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    terms: requiredBoolean(messages.required),
  });
};

export const RATE_FEEDBACK_TAG_KEYS = [
  'nice_to_work',
  'kept_agreements',
  'good_attention',
  'need_better_communication',
  'deadlines_missed',
  'different_role',
  'not_enough_attention',
] as const;

const optionalBoolean = (): ValidatedField => ({
  schema: z.boolean().optional(),
  isRequired: false,
});

export const buildRateFeedbackFormSchema = (t: TFunction): FormSchema => {
  return buildFormSchema(
    {
      comments: optionalString(),
      nice_to_work: optionalBoolean(),
      kept_agreements: optionalBoolean(),
      good_attention: optionalBoolean(),
      need_better_communication: optionalBoolean(),
      deadlines_missed: optionalBoolean(),
      different_role: optionalBoolean(),
      not_enough_attention: optionalBoolean(),
    },
    (schema) =>
      schema.superRefine((data, ctx) => {
        const hasSelection = RATE_FEEDBACK_TAG_KEYS.some(
          (key) => data[key] === true,
        );
        if (!hasSelection) {
          ctx.addIssue({
            code: 'custom',
            path: ['nice_to_work'],
            message: t('VALIDATION_SELECT_AT_LEAST_ONE'),
          });
        }
      }),
  );
};

export const buildInviteEmailTemplateFormSchema = (
  t: TFunction,
): FormSchema => {
  return buildFormSchema({
    content_html: requiredString(t('INVITE_EMAIL_TEMPLATE_BODY_REQUIRED')),
  });
};
